import React, {useEffect, useRef, useState} from 'react';
import MonacoEditor from '@monaco-editor/react';
import {LockClosedIcon} from '@heroicons/react/outline';

import {useAnalytics, useAppContext} from '../../hooks';
import {Panel, PanelGroup, PanelResizer} from '../Panel';
import Button, {ButtonTypes} from '../ButtonNew';
import DropdownInput from '../DropdownInput';
import CodeEngine from './CodeEngine';
import SQLEngine from './SQLEngine';
import ConsolePanel from './Console';
import {toMonacoLanguage} from '../../util';

// TEMP: While we migrate to this editor, we'll disable
// it for lessons that already contain <SQLEditor>.
// Can remove `hasEditor` after updating content in CMS.
export const shouldShowEditor = (lesson) => {
  const isCodingQuestion = lesson?.lessonType === 'question' && !!lesson?.codeProblem;
  const hasEditor = lesson?.intro?.includes('SQLEditor');
  return isCodingQuestion && !hasEditor;
};

// Returns the correct 'engine' based on the lesson type
export const getEditor = (lesson) => {
  if (lesson?.codeProblem?.sqlCode) return SQLEngine;
  if (lesson?.codeProblem) return CodeEngine;
  return null;
};

export default function EditorPanel({
  lesson,
  onEditCode,
  onRunCode,
  onRunTests,
  onPassTests,
  onShareCode,
}) {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();
  const [showConsole, setShowConsole] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const consoleRef = useRef(null);

  // Handles collapsing/expanding console panel on user input
  const onToggleConsole = () => {
    if (showConsole) {
      consoleRef?.current?.collapse();
    } else {
      consoleRef?.current?.expand();
    }
  };

  // Handles updates to collapse status of console panel
  const onConsoleCollapse = (collapsed) => {
    setShowConsole(!collapsed);
  };

  const handleEditCode = ({language, ...rest}) => {
    if (!hasEdited) {
      onEditCode?.({language, ...rest});
      setHasEdited(true);
    }
  };

  const handleRunCode = ({language, ...rest}) => {
    consoleRef?.current?.expand();
    onRunCode?.({language, ...rest});
  };

  const handleRunTests = ({language, ...rest}) => {
    consoleRef?.current?.expand();
    onRunTests?.({language, ...rest});
  };

  const handlePassTests = ({language, ...rest}) => {
    onPassTests?.({language, ...rest});
  };

  const handleShareCode = (language, content) => {
    onShareCode?.({language, content});
  };

  // Ensures the console panel is initially collapsed on mount
  useEffect(() => {
    consoleRef?.current?.collapse();
  }, [consoleRef.current]);

  useEffect(() => {
    if (!lesson) return;
    tracker.codeEditorView({lesson: lesson.slug, difficulty: lesson.difficulty, type: 'panel'});
    setHasEdited(false);
  }, [lesson]);

  // Returns correct editor type based on lesson
  const Editor = getEditor(lesson);

  return Boolean(shouldShowEditor(lesson) && Editor) && (
    <Editor
      code={lesson.codeProblem}
      key={lesson.slug}
      onEditCode={handleEditCode}
      onRunCode={handleRunCode}
      onRunTests={handleRunTests}
      onPassTests={handlePassTests}
    >
      {({
        content,
        languageOptions,
        activeLanguage,
        isRunning,
        isTesting,
        hasTests,
        testResults,
        consoleResults,
        onChange,
        onSubmit,
        onTest,
        onChangeLanguage,
        onReset,
        dirty,
      }) => (
        <div className="flex flex-col bg-white h-full w-full">
          <div className="w-full py-3 px-4 flex justify-between items-start border-b border-gray-200">
            <DropdownInput
              options={languageOptions}
              value={activeLanguage}
              onChange={onChangeLanguage}
              showReset={false}
              highlightActive={false}
              className="mr-2"
              disabled={languageOptions.length < 2}
              native
            />
            {dirty && (
              <Button
                buttonType={ButtonTypes.TERTIARY}
                onClick={onReset}
              >
                Reset
              </Button>
            )}
          </div>
          <PanelGroup direction="vertical">
            <Panel>
              <MonacoEditor
                language={toMonacoLanguage(activeLanguage)}
                value={content}
                onChange={onChange}
                options={{
                  minimap: {
                    enabled: false,
                  },
                  automaticLayout: true,
                  lineNumbers: 'off',
                  padding: {top: 8},
                  lineDecorationsWidth: 0,
                  scrollbar: {
                    vertical: 'hidden',
                  },
                  fontSize: '14px',
                  renderLineHighlight: 'none',
                }}
              />
            </Panel>
            <Panel collapsible ref={consoleRef} onCollapse={onConsoleCollapse}>
              <PanelResizer direction="vertical" />
              <ConsolePanel
                hasTests={hasTests}
                isRunning={isRunning}
                isTesting={isTesting}
                consoleResults={consoleResults}
                testResults={testResults}
              />
            </Panel>
          </PanelGroup>
          {testResults?.failed === 0 && testResults?.passed > 0 && lesson?.question?.id && (
            <div className="px-4 py-2">
              <div className="flex justify-between items-center bg-indigo-100 py-2 px-4 rounded-lg text-indigo font-semibold">
                Well done! Share your solution to get peer feedback
                <Button onClick={() => handleShareCode(activeLanguage, content)}>
                  Share
                </Button>
              </div>
            </div>
          )}
          <div className="w-full py-3 px-4 flex justify-between border-t border-gray-200 flex-wrap">
            <Button
              buttonType={ButtonTypes.TERTIARY}
              onClick={onToggleConsole}
              className="pl-0 pr-2 mb-2"
            >
              Console
            </Button>
            {currentUser ? (
              <div className="ml-auto">
                <Button
                  onClick={onSubmit}
                  disabled={isRunning || isTesting}
                  loading={isRunning}
                  buttonType={hasTests ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY}
                  className="mr-2 mb-2"
                >
                  Run code
                </Button>
                {hasTests && (
                  <Button
                    onClick={onTest}
                    disabled={isRunning || isTesting}
                    loading={isTesting}
                    className="mb-2"
                  >
                    Run tests
                  </Button>
                )}
              </div>
            ) : (
              <Button
                href={currentUser ? '/upgrade?src=lesson' : '/signup?src=lesson'}
                buttonType={ButtonTypes.SECONDARY}
              >
                <LockClosedIcon className="h-4 w-4 mr-1 -ml-1" />
                Sign up to run code
              </Button>
            )}
          </div>
        </div>
      )}
    </Editor>
  );
}
